// SPDX-License-Identifier: AGPL-3.0-or-later

import {areAvatarCostumesEnabled} from '@app/features/ui/avatars/AvatarCostumeFeatureFlag';
import type {ComponentType, SVGProps} from 'react';

export type AvatarCostumePlacement = 'topper' | 'ring' | 'face-overlay';

/**
 * Shared costume fields. `offsetYRatio` is a fraction of costume height:
 * topper is from the avatar **top**; ring and face-overlay are from the avatar **center**.
 */
export interface AvatarCostumeBase {
	id: string;
	placement: AvatarCostumePlacement;
	widthRatio: number;
	aspectRatio: number;
	offsetXRatio: number;
	offsetYRatio: number;
	/**
	 * Optional hand-tuned ink top as a fraction of costume height (0 = viewBox top).
	 * When set (with optional `inkBottomRatio`), skips getBBox measurement for overhang.
	 */
	inkTopRatio?: number;
	/** Optional hand-tuned ink bottom as a fraction of costume height (1 = viewBox bottom). */
	inkBottomRatio?: number;
	/**
	 * Floor for `--avatar-costume-fit` when scrunching. Omit for the CSS default (0.55).
	 * Tall outliers (e.g. wizard) may need a lower floor to clear the avatar above.
	 */
	minFit?: number;
	/**
	 * Fit/scroll scaleY only the part above the avatar. Use when a brim, headband,
	 * or tail sits on the face and a crown/ears stick up.
	 */
	scaleAboveAvatar?: boolean;
	/** CSS `scaleX(-1)` around the costume center. Catalog `id` stays unflipped. */
	flipHorizontal?: boolean;
	/** Contrast halo. Omit for the default `--text-primary` halo. */
	halo?: 'none';
}

export interface AvatarCostumePaints {
	accent: string;
	accentDark: string;
	/** Bunny interiors, hat bands, and other soft secondary fills. */
	accentSoft?: string;
}

export interface AvatarCostumeColorOption extends AvatarCostumePaints {
	id: string;
}

/**
 * `silhouette`: we tint the SVG. `full-color`: keep the file’s own fills (even if it is an SVG URL).
 * `accented`: React SVG with `--costume-accent` / `--costume-accent-dark` / optional `--costume-accent-soft`.
 * `partHalo`: root has no whole-SVG halo; `.costume-part-halo` paths get the drop-shadow (e.g. catface ears).
 */
export type AvatarCostumeDescriptor =
	| (AvatarCostumeBase & {
			art: 'silhouette';
			Svg: ComponentType<SVGProps<SVGSVGElement>>;
			tint: string;
	  })
	| (AvatarCostumeBase & {
			art: 'full-color';
			src: string;
	  })
	| (AvatarCostumeBase & {
			art: 'accented';
			Svg: ComponentType<SVGProps<SVGSVGElement>>;
			accent: string;
			accentDark: string;
			accentSoft?: string;
			/** Halo only on SVG parts marked `costume-part-halo` (Default catface ears). */
			partHalo?: boolean;
			/** Set by contrast resolve when `partHalo` ears need a glow. */
			partHaloActive?: boolean;
			/** Outer silhouette slot for contrast. Omit to use `accent`. */
			haloFill?: 'accent' | 'accentDark';
	  });

export interface AvatarCostumeResolveInput {
	userId: string;
	size: number;
}

export const AVATAR_COSTUME_MIN_SIZE = 32;

type AvatarCostumeResolver = (input: AvatarCostumeResolveInput) => AvatarCostumeDescriptor | null;

const resolvers: Array<AvatarCostumeResolver> = [];
const listeners = new Set<() => void>();

export function notifyAvatarCostumeResolversChanged(): void {
	for (const listener of listeners) {
		listener();
	}
}

export function subscribeAvatarCostumeResolver(resolver: AvatarCostumeResolver): () => void {
	resolvers.push(resolver);
	notifyAvatarCostumeResolversChanged();
	return () => {
		const index = resolvers.indexOf(resolver);
		if (index !== -1) resolvers.splice(index, 1);
		notifyAvatarCostumeResolversChanged();
	};
}

export function subscribeAvatarCostumeResolversChanged(listener: () => void): () => void {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

export function resolveAvatarCostume(input: AvatarCostumeResolveInput): AvatarCostumeDescriptor | null {
	if (input.size < AVATAR_COSTUME_MIN_SIZE) return null;
	if (!areAvatarCostumesEnabled()) return null;
	for (const resolver of resolvers) {
		const costume = resolver(input);
		if (costume != null) return costume;
	}
	return null;
}
